/**
 * Bilan de la semaine, tel que le visuel de partage le présente.
 *
 * Rien n'y est calculé d'une façon nouvelle : moyennes, écarts, maintien et
 * tendance sortent des fonctions qui alimentent le tableau de bord, si bien
 * qu'une image partagée ne peut pas contredire l'écran dont elle vient.
 */

import {
  estimateEnergy,
  goalBand,
  movingAverage,
  trendAt,
  verdict,
  weeklySummaries,
  type BodyProfile,
  type DayRow,
  type Goal,
  type VerdictKind,
} from './stats.js';

/** Une semaine : le bloc qui annule le rythme hebdomadaire du poids. */
export const SHARE_DAYS = 7;

export interface ShareSummary {
  /**
   * Les sept dernières lignes, aujourd'hui compris, dans l'ordre du temps.
   * Même convention que partout ailleurs : la pesée est celle du matin, les
   * pas et les calories ceux de la veille.
   */
  days: DayRow[];
  from: Date;
  to: Date;
  goal: Goal;
  weight: {
    /** Moyenne des pesées de la semaine, `null` sous quatre pesées. */
    mean: number | null;
    /**
     * Écart à la moyenne des sept jours précédents. Sans verdict : d'une
     * semaine à l'autre il reste trop bruité pour trancher seul, et l'image
     * afficherait un écart « hors cible » au-dessus d'une tendance dans la
     * cible. C'est la tendance, avec sa marge, qui juge.
     */
    delta: number | null;
    /** Pesées effectivement saisies sur la semaine. */
    days: number;
    /**
     * Moyenne glissante 7 jours à chacune des journées affichées — le trait
     * épais de l'évolution comparée. Celle d'aujourd'hui est `mean`.
     */
    average: Array<number | null>;
  };
  steps: {
    mean: number | null;
    delta: number | null;
  };
  kcal: {
    mean: number | null;
    /** Maintien du tableau de bord, arrêté à aujourd'hui. */
    maintenance: number;
    /** Apport moyen de la semaine moins le maintien : négatif = déficit. */
    gap: number | null;
  };
  /** Tendance de la carte du même nom, sur la fenêtre qu'elle affiche. */
  trend: {
    window: number;
    /** `null` quand la tendance s'abstient, faute de pesées. */
    slope: {
      perWeek: number;
      /** Demi-largeur de l'IC 95 %, en kg/semaine. */
      margin: number;
      verdict: VerdictKind;
      text: string;
    } | null;
  };
}

export function shareSummary(
  series: DayRow[],
  profile: BodyProfile,
  goal: Goal,
  window: number,
): ShareSummary {
  const days = series.slice(-SHARE_DAYS);
  const last = series.length - 1;

  // Deux blocs de sept jours se terminant aujourd'hui : exactement les deux
  // premières lignes du bilan par semaine.
  const [current, previous] = weeklySummaries(series, 2);

  // Calculée sur toute la série : la moyenne du premier jour affiché remonte
  // six jours avant lui.
  const average = movingAverage(series, SHARE_DAYS).slice(-SHARE_DAYS);

  const stepsMean = current?.steps ?? null;
  const previousSteps = previous?.steps ?? null;

  const kcalMean = current?.kcal ?? null;
  const energy = estimateEnergy(series, last, profile);

  const trend = trendAt(series, last, window);
  let slope: ShareSummary['trend']['slope'] = null;
  if (trend.ok) {
    const assessment = verdict(
      trend.perWeek,
      goalBand(goal, trend.weight),
      goal,
      trend.marginPerWeek,
    );
    slope = {
      perWeek: trend.perWeek,
      margin: trend.marginPerWeek,
      verdict: assessment.kind,
      text: assessment.text,
    };
  }

  const first = days[0];
  const final = days[days.length - 1];
  const today = new Date();

  return {
    days,
    from: first?.date ?? today,
    to: final?.date ?? today,
    goal,
    weight: {
      mean: current?.weight ?? null,
      delta: current?.delta ?? null,
      // Compté sur les lignes affichées : le décompte et le graphe d'à côté
      // ne peuvent pas se contredire.
      days: days.filter((row) => row.weight != null).length,
      average,
    },
    steps: {
      mean: stepsMean,
      delta: stepsMean != null && previousSteps != null ? stepsMean - previousSteps : null,
    },
    kcal: {
      mean: kcalMean,
      maintenance: energy.tdee,
      gap: kcalMean == null ? null : kcalMean - energy.tdee,
    },
    trend: { window, slope },
  };
}
